<template>
    <form class="f fv c-el-panel-bg ath-global-modal-el-info" :class="['el--' + uiStyle]">
        <header class="f fv fa-c fj-l sp-l-l sp-t-l sp-r-l sp-b-m el-modal-header" :class="uiHeaderClass">
            <i class="ta-c" :class="'lnr-' + uiIcon" v-if="uiIcon" aria-hidden="true"></i>
            <img :src="uiSvg" v-if="uiSvg" width="60" height="60" aria-hidden="true" />
            <h2 class="el-modal-title ta-c">
                <span v-if="uiTitle && !uiTitleEntity">
                    {{ uiTitle | athT(uiTitleParams) }}
                </span>
                <span v-if="uiTitle && uiTitleEntity">
                    {{ uiTitle | athTEnt(uiTitleEntity, uiTitleEntityLc) }}
                </span>
                <slot name="title"></slot>
            </h2>
            <slot name="headerAfter"></slot>
        </header>
        <main class="f fv sp-l el-modal-body" :class="uiBodyClass">
            <p class="te-be ta-c o-pml" v-if="uiText" v-html="$options.filters.athT(uiText, uiTextParams)">
            </p>
            <slot name="body"></slot>
        </main>
        <footer class="f fh sp-l el-modal-footer">
            <slot name="footer"></slot>
            <AthButton v-if="uiCta"
                :lbl="uiCta"
                :uiAutoFocus="uiCtaAutoFocus"
                class="el--s-m el--t-default"
                @click="close" />
        </footer>
    </form>
</template>

<script>
import AthButton from '../../atoms/Button.vue';

export default {
    name: 'mead-info-modal',
    components: {AthButton},
    emits: ['onContinue'],
    props: {
        /**
         * URL of an image/SVG shown in the header. Ignored when falsy; takes
         * precedence over uiIcon when both are set.
         */
        uiSvg           : {type: [Boolean, String], default: () => false},
        /**
         * Icon font class suffix (rendered as `lnr-{uiIcon}`) shown in the header
         * when uiSvg is not set. False hides the icon.
         */
        uiIcon          : {type: [Boolean, String], default: () => false},
        /**
         * Title text or localization key shown in the header. False hides the title.
         */
        uiTitle         : {type: [Boolean, String], default: () => false},
        /**
         * Interpolation parameters passed to the athT translation filter when
         * rendering uiTitle.
         */
        uiTitleParams   : {type: Object, default: () => ({})},
        /**
         * Entity name used to translate uiTitle via the athTEnt filter instead of
         * athT, for entity-specific title phrasing. False disables entity translation.
         */
        uiTitleEntity   : {type: [Boolean, String], default: () => false},
        /**
         * Whether uiTitleEntity should be lowercased when interpolated by athTEnt.
         */
        uiTitleEntityLc : {type: Boolean, default: () => false},
        /**
         * Body text or localization key rendered (via v-html) as the modal's main
         * message. False hides this text.
         */
        uiText          : {type: [Boolean, String], default: () => false},
        /**
         * Interpolation parameters passed to the athT translation filter when
         * rendering uiText.
         */
        uiTextParams    : {type: Object, default: () => ({})},
        /**
         * Additional CSS class(es) applied to the header element.
         */
        uiHeaderClass   : {type: String, default: () => ''},
        /**
         * Additional CSS class(es) applied to the body/main element.
         */
        uiBodyClass     : {type: String, default: () => ''},
        /**
         * Label or localization key for the call-to-action button shown in the
         * footer. False hides the button.
         */
        uiCta           : {type: [Boolean, String], default: () => 'mead.labels.continue'},
        /**
         * Whether the call-to-action button should automatically receive focus
         * when the modal is shown.
         */
        uiCtaAutoFocus  : {type: Boolean, default: () => true},
        /**
         * Width of the modal, in rem units, applied directly to the root element's style.
         */
        uiWidth         : {type: [Number, String], default: () => 35},
        /**
         * Style variant modifier (rendered as `el--{uiStyle}`) applied to the root
         * element, controlling its color scheme (e.g. 'success').
         */
        uiStyle         : {type: String, default: () => 'success'},
    },
    watch: {
        uiWidth: function () {
            this.setWidth();
        },
    },
    methods: {
        setWidth: function () {
            if (!this.$el || !this.uiWidth) return;
            this.$el.style.width = `${this.uiWidth}rem`;
        },
        close: function () {
            this.$emit('onContinue');
            MeadCommon.ModalService.close();
        },
    },
    mounted () {
        this.setWidth();
    },
};
</script>
<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

.ath-global-modal-el-info {
    p {
        font-size: $fs-m;
    }

    @media screen and (min-width: $mq-ds-break) {
        .el-modal-footer {
            flex-direction: row;

            .el-btn {
                width: auto;
                flex-grow: 1;

                &:not(:last-of-type) {
                    margin-right: $s-xs;
                }

                &:not(:first-of-type) {
                    margin-left: $s-xs;
                }
            }
        }

        //  STYLE MODIFIERS

        &.el--success {
            background-color: var(--c-brand-secondary-bg);
            color: var(--c-brand-secondary-fg);

            *:not(.el--nostyleoverride) i:not(.icon-facebook):not(.icon-google),
            *:not(.el--nostyleoverride) h2,
            *:not(.el--nostyleoverride) p {
                color: var(--c-brand-secondary-fg);
            }
        }
    }

    @media screen and (max-width: $mq-ds) {
        .el-modal-footer {
            flex-direction: column;

            .el-btn {
                width: 100%;

                &:not(:last-of-type) {
                    margin-bottom: $s-s;
                }
            }
        }
    }

    .el-modal-header i,
    .el-modal-header img {
        display: block;
        height: $fs-hero;
        line-height: $fs-hero;
        font-size: $fs-hero;
        margin: $s-s auto $s-l auto;
        animation: upDown .5s infinite alternate ease;
    }

    .el-modal-header i {
        width: 100%;
    }

}

</style>
